import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import API from '../config/api';
import { db } from '../firebaseConfig';
import DashboardHeader from './DashboardHeader';
import DashboardSidebar from './DashboardSidebar';
import { useClinicContext } from './ClinicContext';
import { useLanguage } from './LanguageContext';
import {
  buildDoctorDailyExportRows,
  buildDoctorDailySummaryRows,
  buildDoctorDailyTotals,
  buildPatientFrequencyRows,
  buildVisitReportRows,
  DoctorDailySummaryRow,
  DoctorAttendanceSession,
  filterReportRowsByDoctor,
  pairDoctorAttendanceLogs,
  PatientFrequencyRow,
  VisitReportRow,
} from '../utils/ownerVisitReports';

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const durationLabel = (minutes: number | null): string => {
  if (minutes == null) return '—';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours}h ${remainder}m`;
};

const OwnerVisitReports: React.FC = () => {
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const { selectedClinic, selectedClinicId } = useClinicContext();
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'visits' | 'frequency' | ''>('');
  const [search, setSearch] = useState('');
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [doctorOptions, setDoctorOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [visitRows, setVisitRows] = useState<VisitReportRow[]>([]);
  const [frequencyRows, setFrequencyRows] = useState<PatientFrequencyRow[]>([]);
  const [attendanceSessions, setAttendanceSessions] = useState<DoctorAttendanceSession[]>([]);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('day'),
  ]);

  const clinicId = selectedClinicId && selectedClinicId !== '__all_clinics__'
    ? selectedClinicId
    : '';
  const clinicName = selectedClinic?.name || clinicId;
  const startDate = range[0].format('YYYY-MM-DD');
  const endDate = range[1].format('YYYY-MM-DD');

  const loadDoctorDirectory = useCallback(async () => {
    if (!clinicId) return { doctorIds: [] as string[], doctorNames: {} as Record<string, string> };
    const assignments = await getDocs(
      query(collection(db, 'doctor_clinic_assistant'), where('clinic_id', '==', clinicId))
    );
    const doctorIds = Array.from(new Set(
      assignments.docs.map((item) => String(item.data().doctor_id || '').trim()).filter(Boolean)
    ));
    const doctorNames: Record<string, string> = {};
    for (let index = 0; index < doctorIds.length; index += 30) {
      const batch = doctorIds.slice(index, index + 30);
      const doctors = await getDocs(query(collection(db, 'doctors'), where('doctor_id', 'in', batch)));
      doctors.docs.forEach((item) => {
        const data = item.data();
        doctorNames[String(data.doctor_id)] = data.name || data.email || data.doctor_id;
      });
    }
    return { doctorIds, doctorNames };
  }, [clinicId]);

  const loadReports = useCallback(async () => {
    if (!clinicId) {
      setVisitRows([]);
      setFrequencyRows([]);
      setAttendanceSessions([]);
      setDoctorOptions([]);
      return;
    }
    setLoading(true);
    try {
      const { doctorIds, doctorNames } = await loadDoctorDirectory();
      setDoctorOptions(doctorIds.map((doctorId) => ({
        value: doctorId,
        label: doctorNames[doctorId] || doctorId,
      })).sort((a, b) => a.label.localeCompare(b.label)));
      if (doctorIds.length === 0) {
        setVisitRows([]);
        setFrequencyRows([]);
        setAttendanceSessions([]);
        return;
      }

      const [reportResponse, visitTypeResponses] = await Promise.all([
        axios.get(`${API.BASE_URL}${API.ENDPOINTS.OWNER_VISIT_REPORT(clinicId)}`, {
          params: {
            doctor_ids: doctorIds.join(','),
            startDate,
            endDate,
          },
          timeout: 60000,
        }),
        Promise.all(doctorIds.map((doctorId) => axios.get(
          `${API.BASE_URL}${API.ENDPOINTS.VISIT_TYPE_CONFIG(doctorId)}`,
          { timeout: 30000 }
        ).catch(() => null))),
      ]);

      if (!reportResponse.data?.success) {
        throw new Error(reportResponse.data?.message || 'Could not load visits');
      }
      const visitTypeLabels: Record<string, string> = {};
      visitTypeResponses.forEach((response) => {
        const types = response?.data?.data?.visit_types || [];
        types.forEach((type: any) => {
          const typeId = String(type?.type_id || '').trim();
          if (typeId && !visitTypeLabels[typeId]) {
            visitTypeLabels[typeId] = String(type?.name_ar || type?.name || typeId).trim();
          }
        });
      });
      const reportData = reportResponse.data.data || {};
      const visits = buildVisitReportRows(reportData.patients || [], doctorNames, startDate, endDate, visitTypeLabels);
      const sessions = pairDoctorAttendanceLogs(reportData.attendanceLogs || [], doctorNames);
      setVisitRows(visits);
      setFrequencyRows(buildPatientFrequencyRows(visits));
      setAttendanceSessions(sessions);
    } catch (error: any) {
      console.error('Failed to load owner visit reports:', error);
      message.error(error?.response?.data?.message || 'Failed to load visit reports');
      setVisitRows([]);
      setFrequencyRows([]);
      setAttendanceSessions([]);
    } finally {
      setLoading(false);
    }
  }, [clinicId, endDate, loadDoctorDirectory, startDate]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    setSelectedDoctorId('');
  }, [clinicId]);

  const queryText = search.trim().toLocaleLowerCase();
  const selectedVisitRows = useMemo(
    () => filterReportRowsByDoctor(visitRows, selectedDoctorId),
    [selectedDoctorId, visitRows]
  );
  const selectedAttendanceSessions = useMemo(
    () => filterReportRowsByDoctor(attendanceSessions, selectedDoctorId),
    [attendanceSessions, selectedDoctorId]
  );
  const selectedFrequencyRows = useMemo(
    () => selectedDoctorId ? buildPatientFrequencyRows(selectedVisitRows) : frequencyRows,
    [frequencyRows, selectedDoctorId, selectedVisitRows]
  );
  const selectedDailyRows = useMemo(
    () => buildDoctorDailySummaryRows(selectedVisitRows, selectedAttendanceSessions),
    [selectedAttendanceSessions, selectedVisitRows]
  );
  const filteredDailyRows = useMemo(() => {
    if (!queryText) return selectedDailyRows;
    return selectedDailyRows.filter((row) => [
      row.date, row.doctorName, row.clinicName,
    ].some((value) => String(value || '').toLocaleLowerCase().includes(queryText)));
  }, [queryText, selectedDailyRows]);
  const filteredFrequencyRows = useMemo(() => {
    if (!queryText) return selectedFrequencyRows;
    return selectedFrequencyRows.filter((row) => [
      row.patientName, row.patientPhone, row.fileNumber, row.doctorNames.join(' '),
    ].some((value) => String(value || '').toLocaleLowerCase().includes(queryText)));
  }, [queryText, selectedFrequencyRows]);
  const dailyTotals = useMemo(
    () => buildDoctorDailyTotals(filteredDailyRows),
    [filteredDailyRows]
  );

  const saveWorkbook = (rows: Record<string, any>[], sheetName: string, filename: string) => {
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ Message: 'No data for selected period' }]);
    XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    XLSX.writeFile(workbook, filename);
  };

  const exportDoctorDailySummary = () => {
    setExporting('visits');
    try {
      saveWorkbook(
        buildDoctorDailyExportRows(filteredDailyRows, clinicName),
        'Doctor Daily Summary',
        `doctor-daily-summary-${startDate}-to-${endDate}.xlsx`,
      );
      message.success('Doctor daily summary downloaded');
    } catch (error) {
      console.error('Failed to export visits and attendance:', error);
      message.error('Could not export the report');
    } finally {
      setExporting('');
    }
  };

  const exportPatientFrequency = () => {
    setExporting('frequency');
    try {
      saveWorkbook(filteredFrequencyRows.map((row) => ({
        Clinic: clinicName,
        'Patient Name': row.patientName,
        'File Number': row.fileNumber,
        Phone: row.patientPhone,
        'Visits Count': row.visitsCount,
        'First Visit': row.firstVisit,
        'Last Visit': row.lastVisit,
        Doctors: row.doctorNames.join(', '),
      })), 'Patient Visit Frequency', `patient-visit-frequency-${startDate}-to-${endDate}.xlsx`);
      message.success('Patient visit-frequency sheet downloaded');
    } catch (error) {
      console.error('Failed to export patient visit frequency:', error);
      message.error('Could not export the report');
    } finally {
      setExporting('');
    }
  };

  const dailyColumns: any[] = [
    { title: 'Date', dataIndex: 'date', key: 'date', width: 120 },
    { title: 'Doctor', dataIndex: 'doctorName', key: 'doctorName', width: 150 },
    { title: 'Cases', dataIndex: 'casesCount', key: 'casesCount', width: 90, sorter: (a: DoctorDailySummaryRow, b: DoctorDailySummaryRow) => a.casesCount - b.casesCount, render: (value: number) => <Tag color="blue">{value}</Tag> },
    {
      title: 'Attendance', dataIndex: 'attendanceStatus', key: 'attendanceStatus', width: 150,
      render: (value: string) => value === 'present'
        ? <Tag color="green">Present</Tag>
        : <Tag>No attendance record</Tag>,
    },
    { title: 'Check In', dataIndex: 'signInTime', key: 'signInTime', width: 95, render: (value: string) => value || '—' },
    { title: 'Check Out', dataIndex: 'signOutTime', key: 'signOutTime', width: 125, render: (value: string) => value || '—' },
    { title: 'Worked', dataIndex: 'workedMinutes', key: 'workedMinutes', width: 95, render: durationLabel },
  ];

  const frequencyColumns: any[] = [
    { title: 'Patient Name', dataIndex: 'patientName', key: 'patientName', sorter: (a: PatientFrequencyRow, b: PatientFrequencyRow) => a.patientName.localeCompare(b.patientName) },
    { title: 'File No.', dataIndex: 'fileNumber', key: 'fileNumber', render: (value: string) => value || '—' },
    { title: 'Phone', dataIndex: 'patientPhone', key: 'patientPhone', render: (value: string) => value || '—' },
    { title: 'Visits Count', dataIndex: 'visitsCount', key: 'visitsCount', defaultSortOrder: 'descend', sorter: (a: PatientFrequencyRow, b: PatientFrequencyRow) => a.visitsCount - b.visitsCount, render: (value: number) => <Tag color="blue">{value}</Tag> },
    { title: 'First Visit', dataIndex: 'firstVisit', key: 'firstVisit' },
    { title: 'Last Visit', dataIndex: 'lastVisit', key: 'lastVisit' },
    { title: 'Doctors', dataIndex: 'doctorNames', key: 'doctorNames', render: (value: string[]) => value.join(', ') || '—' },
  ];

  const tabs = [
    {
      key: 'visits-attendance',
      label: 'Doctor Daily Attendance',
      children: (
        <Card
          title="Daily doctor attendance and cases"
          extra={<Button type="primary" icon={<FileExcelOutlined />} onClick={exportDoctorDailySummary} loading={exporting === 'visits'} disabled={loading}>Export daily summary</Button>}
        >
          <Alert
            type="info"
            showIcon
            message="One row per doctor per day: first check-in, last check-out, total worked time, and completed cases. Attendance-only days appear with zero cases."
            style={{ marginBottom: 16 }}
          />
          <Table
            columns={dailyColumns}
            dataSource={filteredDailyRows}
            rowKey="key"
            scroll={{ x: 850 }}
            pagination={{ pageSize: 25, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="No visits or attendance in this period" /> }}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ background: '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text strong>Total for selected period</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <Tag color="blue">{dailyTotals.casesCount}</Tag>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <Text strong>{dailyTotals.attendanceDays} days</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} colSpan={2}>—</Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    <Text strong>{durationLabel(dailyTotals.workedMinutes)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </Card>
      ),
    },
    {
      key: 'patient-frequency',
      label: 'Patient Visit Frequency',
      children: (
        <Card
          title="How many times each patient visited"
          extra={<Button type="primary" icon={<FileExcelOutlined />} onClick={exportPatientFrequency} loading={exporting === 'frequency'} disabled={loading}>Export frequency sheet</Button>}
        >
          <Table
            columns={frequencyColumns}
            dataSource={filteredFrequencyRows}
            rowKey="key"
            scroll={{ x: 900 }}
            pagination={{ pageSize: 25, showSizeChanger: true }}
            locale={{ emptyText: <Empty description="No patient visits in this period" /> }}
          />
        </Card>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh', direction: isRTL ? 'rtl' : 'ltr' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout>
        <DashboardHeader onSettingsClick={() => navigate('/settings')} onMenuClick={() => {}} isMobile={false} />
        <Content style={{ margin: '24px 16px', padding: 24 }}>
          <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col>
              <Space align="center">
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard')} type="text" />
                <CalendarOutlined style={{ fontSize: 22, color: '#1677ff' }} />
                <div>
                  <Title level={3} style={{ margin: 0 }}>Owner Visit Reports</Title>
                  <Text type="secondary">Two separate reports using the same selected period</Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button icon={<ReloadOutlined />} onClick={loadReports} loading={loading}>Refresh</Button>
            </Col>
          </Row>

          {!clinicId ? (
            <Alert type="info" showIcon message="Choose one clinic from the header to view owner reports." />
          ) : (
            <>
              <Card size="small" style={{ marginBottom: 18 }}>
                <Row gutter={[12, 12]} align="middle">
                  <Col><Text strong>Report period:</Text></Col>
                  <Col>
                    <RangePicker
                      allowClear={false}
                      value={range}
                      onChange={(value) => value?.[0] && value?.[1] && setRange([value[0], value[1]])}
                    />
                  </Col>
                  <Col><Button onClick={() => setRange([dayjs().startOf('day'), dayjs().endOf('day')])}>Today</Button></Col>
                  <Col><Button onClick={() => setRange([dayjs().startOf('month'), dayjs().endOf('day')])}>This month</Button></Col>
                  <Col>
                    <Select
                      aria-label="Doctor filter"
                      value={selectedDoctorId}
                      onChange={setSelectedDoctorId}
                      options={[{ value: '', label: 'All doctors' }, ...doctorOptions]}
                      style={{ minWidth: 220 }}
                      showSearch
                      optionFilterProp="label"
                    />
                  </Col>
                  <Col flex="auto">
                    <Input.Search
                      allowClear
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search doctor/date or patient/file in frequency"
                      style={{ maxWidth: 360, float: isRTL ? 'left' : 'right' }}
                    />
                  </Col>
                </Row>
              </Card>

              <Spin spinning={loading}>
                <Row gutter={[12, 12]} style={{ marginBottom: 18 }}>
                  <Col xs={12} md={6}><Card size="small"><Statistic title="Visits" value={selectedVisitRows.length} prefix={<CalendarOutlined />} /></Card></Col>
                  <Col xs={12} md={6}><Card size="small"><Statistic title="Patients" value={selectedFrequencyRows.length} prefix={<UserOutlined />} /></Card></Col>
                  <Col xs={12} md={6}><Card size="small"><Statistic title="Doctors with visits" value={new Set(selectedVisitRows.map((row) => row.doctorId)).size} prefix={<TeamOutlined />} /></Card></Col>
                  <Col xs={12} md={6}><Card size="small"><Statistic title="Attendance sessions" value={selectedAttendanceSessions.length} /></Card></Col>
                </Row>
                <Tabs items={tabs} />
              </Spin>
            </>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default OwnerVisitReports;
